/**
 * Strips Outlook-specific VML and noise from bodyHtml before POSTing to the API.
 * The API enforces a 65KB limit on bodyHtml — this keeps payloads manageable.
 *
 * NOT a security sanitizer — that lives server-side.
 * This is purely a size/fidelity optimization.
 */
export function sanitizeOutlookHtml(html: string | null): string | null {
  if (!html) return null

  let out = html

  // Remove VML namespaced elements (<v:roundrect>, <o:p>, etc.)
  out = out.replace(/<\/?[vo]:[a-z]+[^>]*>/gi, '')

  // Remove <style> blocks — they're massive and useless for storage
  out = out.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')

  // Remove XML processing instructions
  out = out.replace(/<\?xml[^>]*\?>/gi, '')

  // Remove MsoNormal and Office-specific class values
  out = out.replace(/\s*class="[^"]*Mso[^"]*"/gi, '')

  // Collapse excessive whitespace runs (but preserve newlines for readability)
  out = out.replace(/[ \t]{3,}/g, '  ')

  // Truncate if still over the API's 65KB limit
  const MAX_BYTES = 65_000
  if (out.length > MAX_BYTES) {
    out = out.slice(0, MAX_BYTES)
    // Close any open tags we might have split
    out += '<!-- [truncated] -->'
  }

  return out.trim() || null
}

/**
 * Extracts a short snippet (≤512 chars) from plain text.
 */
export function makeSnippet(bodyText: string | null): string | undefined {
  if (!bodyText) return undefined
  return bodyText.trim().slice(0, 512) || undefined
}

/**
 * Truncates bodyText to fit within the API's 65536 char limit.
 */
export function truncateBodyText(bodyText: string | null): string | undefined {
  if (!bodyText) return undefined
  const trimmed = bodyText.trim()
  if (!trimmed) return undefined
  return trimmed.length > 65_000 ? trimmed.slice(0, 65_000) : trimmed
}
